import { alpha, createTheme, type Theme } from '@mui/material/styles';

import {
  brandPalette,
  breakpoints,
  gradients,
  motion,
  radii,
  statusPalette,
  surfaces,
  typography,
  webShadows,
} from './tokens';

export type ThemeMode = 'light' | 'dark';

/** Re-export brand gradients so feature code can pull them without reaching into `tokens`. */
export { gradients };

/**
 * Builds the MUI theme for a given mode from the shared design tokens (§7.3, §7.6).
 *
 * Design System V2: this is the single place the premium visual language is applied to MUI —
 * rounded geometry, soft layered shadows, obvious focus rings, subtle hover motion, a translucent
 * blurred app bar, and pill navigation. Every persona portal inherits it automatically, so a future
 * Recruiter/Company/Vendor/Admin screen looks premium without touching this file again.
 */
export function createAppTheme(mode: ThemeMode): Theme {
  const isDark = mode === 'dark';
  const s = surfaces[mode];
  const sh = webShadows[mode];
  const primaryMain = brandPalette.primary.main;

  return createTheme({
    palette: {
      mode,
      primary: brandPalette.primary,
      secondary: brandPalette.secondary,
      accent: {
        main: brandPalette.accent.main,
        light: brandPalette.accent.light,
        dark: brandPalette.accent.dark,
        contrastText: brandPalette.accent.contrastText,
      },
      background: {
        default: s.background,
        paper: s.surface,
      },
      text: {
        primary: s.textPrimary,
        secondary: s.textSecondary,
        disabled: s.textTertiary,
      },
      divider: s.border,
      success: { main: statusPalette.success },
      warning: { main: statusPalette.warning },
      error: { main: statusPalette.error },
      info: { main: statusPalette.info },
    },
    breakpoints,
    shape: {
      borderRadius: radii.md,
    },
    typography: {
      fontFamily: typography.fontFamily,
      h1: { fontWeight: typography.fontWeights.bold, ...typography.scale.h1 },
      h2: { fontWeight: typography.fontWeights.bold, ...typography.scale.h2 },
      h3: { fontWeight: typography.fontWeights.bold, ...typography.scale.h3 },
      h4: { fontWeight: typography.fontWeights.semibold, ...typography.scale.h4 },
      h5: { fontWeight: typography.fontWeights.semibold, ...typography.scale.h5 },
      h6: { fontWeight: typography.fontWeights.semibold, ...typography.scale.h6 },
      body1: typography.scale.body1,
      body2: typography.scale.body2,
      caption: typography.scale.caption,
      overline: { fontWeight: typography.fontWeights.semibold, ...typography.scale.overline },
      button: {
        textTransform: 'none',
        fontWeight: typography.fontWeights.semibold,
        letterSpacing: '0.01em',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': { colorScheme: mode },
          body: {
            backgroundColor: s.background,
            color: s.textPrimary,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': { backgroundColor: alpha(primaryMain, 0.24) },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: s.borderStrong,
            borderRadius: 999,
            border: `2px solid ${s.background}`,
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: s.textTertiary },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        },
      },

      MuiPaper: {
        styleOverrides: {
          // Kill MUI's dark-mode lightening overlay so our flat premium surface colors stay true.
          root: { backgroundImage: 'none' },
        },
      },

      MuiButton: {
        defaultProps: { disableRipple: false },
        styleOverrides: {
          root: {
            borderRadius: radii.md,
            textTransform: 'none',
            fontWeight: typography.fontWeights.semibold,
            transition: motion.hover,
            '&:focus-visible': { boxShadow: sh.focusPrimary },
          },
          sizeLarge: { minHeight: 48, paddingInline: 22, fontSize: '0.9375rem' },
          sizeMedium: { minHeight: 42, paddingInline: 18 },
          sizeSmall: { minHeight: 34, paddingInline: 14 },
          contained: {
            boxShadow: sh.card,
            '&:hover': { boxShadow: sh.raised, transform: 'translateY(-1px)' },
            '&:active': { transform: 'translateY(0)', boxShadow: sh.card },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5, backgroundColor: alpha(primaryMain, 0.06) },
          },
          text: {
            '&:hover': { backgroundColor: alpha(primaryMain, 0.06) },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: radii.md, transition: motion.hover },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: radii.lg,
            backgroundImage: 'none',
            transition: motion.hover,
            ...(ownerState.variant === 'outlined'
              ? { border: `1px solid ${s.border}`, boxShadow: 'none' }
              : { border: 'none', boxShadow: sh.card }),
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: 24, paddingBottom: 8 },
          title: { fontWeight: typography.fontWeights.semibold, fontSize: '1.0625rem' },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radii.md,
            backgroundColor: isDark ? alpha(s.elevated, 0.5) : '#FFFFFF',
            transition: motion.hover,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: s.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: s.borderStrong },
            // No focus ring on inputs. `Mui-focused` applies to pointer focus too, so a glow here
            // haloed every field the moment it was clicked into. The notched outline below carries
            // the focus state on its own — colour AND width both change, so focus stays visible
            // without a shadow. Buttons keep their `:focus-visible` ring (keyboard-only, and they
            // have no border to change).
            // 2px, not 1.5. The notch in an outlined field is a real <fieldset>/<legend> border
            // break, and the legend's 11px height is sized against a whole-pixel border. A
            // fractional width rounds inconsistently along that break, so the top border bleeds
            // across the gap and paints a hairline straight through the floating label — it reads
            // as struck-through text. 2 is MUI's own focused width for exactly this reason.
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: primaryMain,
            },
            // Both of the above are two-class selectors, so this override and MUI's own `.Mui-error`
            // rule tie on specificity and source order decides — which made a focused invalid field
            // go primary-purple. The red glow used to mask that; with the glow gone the field needs
            // to stay red on its own, so this three-class rule wins outright.
            '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: statusPalette.error,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { '&.Mui-focused': { color: primaryMain } },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radii.pill, fontWeight: typography.fontWeights.medium },
          label: { paddingInline: 10 },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radii.xl, boxShadow: sh.dialog, backgroundImage: 'none' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: typography.fontWeights.bold, fontSize: '1.25rem' },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radii.lg,
            boxShadow: sh.menu,
            border: `1px solid ${s.border}`,
            marginTop: 6,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: radii.lg, boxShadow: sh.menu },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { borderRadius: radii.sm, marginInline: 6, minHeight: 40 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radii.md,
            marginBlock: 2,
            transition: motion.hover,
            '&:hover': { backgroundColor: s.hoverOverlay },
            '&.Mui-selected': {
              backgroundColor: s.selectedOverlay,
              '&:hover': { backgroundColor: s.selectedOverlay },
            },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: radii.sm,
            backgroundColor: isDark ? '#2B2740' : '#1E293B',
            fontSize: '0.75rem',
            fontWeight: typography.fontWeights.medium,
            padding: '6px 10px',
          },
          arrow: { color: isDark ? '#2B2740' : '#1E293B' },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: alpha(s.surface, isDark ? 0.72 : 0.8),
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${s.border}`,
            boxShadow: 'none',
            color: s.textPrimary,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: s.border },
          head: {
            fontWeight: typography.fontWeights.semibold,
            color: s.textSecondary,
            backgroundColor: isDark ? s.elevated : s.background,
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { transition: motion.hover, '&:hover': { backgroundColor: s.hoverOverlay } },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: 3 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: typography.fontWeights.semibold,
            minHeight: 44,
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: radii.pill,
            height: 8,
            backgroundColor: alpha(primaryMain, isDark ? 0.2 : 0.12),
          },
          bar: { borderRadius: radii.pill },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: typography.fontWeights.semibold },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radii.md },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: s.border },
        },
      },
    },
  });
}
